import { useIsAdmin } from '../../auth/InsightsAccountContext'
import { usePeriod } from '../../payload/PeriodContext'
import {
  Card,
  CardHead,
  DataTable,
  Note,
  SectionHead,
  Td,
  Tr,
} from '../components'
import { n, num, plural, s } from '../format'

/**
 * THE ONLY BLOCK THAT SHOWS NAMES AND EMAIL ADDRESSES, and only for our own
 * accounts. The query enforces the boundary (it selects the internal classes
 * only) and the callable serves the panel to admins only; this renders nothing
 * unless both hold.
 */
const Team = () => {
  const isAdmin = useIsAdmin()
  const { panel, raw } = usePeriod()
  if (!isAdmin || !raw('team_accounts').length) return null

  const rows = panel('team_accounts')
  const groups = [...new Set(rows.map(r => s(r.team_group)))]
  // Matched on the full group name rather than on the short one shown in the
  // column, which is a domain for staff rows and would never match.
  const inferred = rows.filter(r =>
    s(r.team_group_full).includes('outside the US')
  )

  return (
    <section className="grid gap-4">
      <SectionHead
        title="Our own accounts"
        question="Who on the team is in production? The only block on this page that names anyone, and it can only name us."
      />

      <Card>
        <CardHead
          title="Team and test accounts"
          info={[
            `${plural(rows.length, 'account')} across ${plural(groups.length, 'group')} that were active in the selected period. Team and test accounts only — real users are never named anywhere on this page.`,
            'Useful for spotting whose abandoned test data is still moving the topic figures, and for checking the accounts we have only guessed at.',
            'Names come from the household profile, so they are what the person typed rather than anything verified, and are often absent.',
          ]}
        />
        {rows.length ? (
          <DataTable
            label="Team and test accounts"
            head={[
              { label: 'Group' },
              { label: 'Name' },
              { label: 'Email' },
              { label: 'Answers saved', num: true },
              { label: 'Topics', num: true },
              { label: 'Joined' },
              { label: 'Last saved' },
              { label: 'Last seen' },
            ]}
          >
            {rows.map((r, i) => (
              <Tr key={`${s(r.email)}-${i}`} dim={n(r.answers_saved) === 0}>
                <Td muted nowrap>
                  {s(r.team_group)}
                </Td>
                <Td nowrap>{s(r.name)}</Td>
                <Td mono>{s(r.email)}</Td>
                <Td num>{num(r.answers_saved)}</Td>
                <Td num>{num(r.topics)}</Td>
                <Td muted nowrap>
                  {s(r.joined) || '—'}
                </Td>
                <Td muted nowrap>
                  {s(r.last_saved) || '—'}
                </Td>
                <Td muted nowrap>
                  {s(r.last_seen) || '—'}
                </Td>
              </Tr>
            ))}
          </DataTable>
        ) : null}
        <Note
          parts={
            inferred.length > 0 && [
              <strong key="lead">
                {`The ${plural(inferred.length, 'row')} marked "Team, outside US" ${inferred.length === 1 ? 'was' : 'were'} checked by hand and confirmed as ours. `}
              </strong>,
              'They are grouped on country rather than on a work email, so they used to be flagged as a guess. They are not one any more — but the rule still is: a genuine non-US customer signing up tomorrow would land in this group and need moving out.',
            ]
          }
        />
      </Card>
    </section>
  )
}

export default Team
